import React from 'react'
import {useTranslation} from 'react-i18next'
import {InvitationCodeRecord} from '../../domain/entities/InvitationCode.ts'
import {useAgencyActivity} from '../../hooks/useAgencyActivity.ts'
import {PackActivityTables} from './PackActivityTables.tsx'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import {formatNumber} from '../../utils/formatNumber.ts'

interface AgencyActivityCardProps {
    agencyId: string
    primaryMarket?: string
    onCodeCreated: () => void
    invitationCodesById: Map<string, InvitationCodeRecord>
    isSelfService?: boolean
    excludeCodeIds?: Set<string>
}

// Agency-wide equivalent of PackCard for pay-per-use agencies: same tables
// (TripBooks impresos / TripBooks creados / Códigos prerellenados), but over
// all the agency's activity instead of a single pack's.
export const AgencyActivityCard: React.FC<AgencyActivityCardProps> = ({agencyId, primaryMarket, onCodeCreated, invitationCodesById, isSelfService, excludeCodeIds}) => {
    const {t} = useTranslation('dashboard')
    const {activity, isLoadingActivity} = useAgencyActivity(agencyId, primaryMarket, true, isSelfService)

    if (isLoadingActivity || !activity) {
        return (
            <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm flex items-center justify-center py-16">
                <IconSpinner className="size-8"/>
            </section>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm">
                <div className="flex items-center gap-2.5 flex-wrap px-[22px] py-[18px]">
                    <span className="text-[15px] font-medium text-neutralBlack-900">{t('packs.agencyActivityTitle')}</span>
                    <span className="flex-1 min-w-[12px]"/>
                    <span className="flex items-center gap-2.5 text-[13px] text-neutralBlack-700 whitespace-nowrap">
                        <span>{formatNumber(activity.counters.created)} {t('packs.activeTrips')}</span>
                        <span className="text-neutralBlack-300">|</span>
                        <span>{formatNumber(activity.counters.printed)} {t('packs.printedShort')}</span>
                        <span className="text-neutralBlack-300">|</span>
                        <span>{formatNumber(activity.counters.inactive)} {t('packs.inactiveCodes')}</span>
                    </span>
                </div>
            </section>

            <PackActivityTables
                agencyId={agencyId}
                printed={activity.printed}
                created={activity.created}
                inactive={activity.inactive}
                primaryMarket={primaryMarket}
                onCodeCreated={onCodeCreated}
                invitationCodesById={invitationCodesById}
                excludeCodeIds={excludeCodeIds}
            />
        </div>
    )
}
